import roles from '@/constants/roles';

const { Base64 } = require('js-base64');

const getDefaultState = (lang = 'en') => ({
    status: '',
    user: {
        lang,
        id: '',
        email: '',
        teams: [],
        teamPagination: {},
        isFirstLogin: false,
    },
    currentMember: {},
    signup: {},
    selectedTeamIndex: -1,
    isLoggedIn: false,
    isFirstLoginSession: true,
    tokenExpiration: 0,
    verificationCode: '',
});

const defaultState = getDefaultState();

const mutations = {
    // Set authentification status
    authRequest(state) {
        state.status = 'loading';
    },
    authSuccess(state, {
        headers, refreshToken, secure, user,
    }) {
        state.status = 'success';
        state.selectedTeam = -1;
        Object.assign(state.user, user);
        if (state.user.teams.length === 1) {
            state.selectedTeamIndex = 0;
        }
        this.$rq.setAxiosHeaders(headers);
        window.$cookies.set(
            'refreshToken',
            refreshToken,
            '30d',
            '/',
            process.env.VUE_CLI_BASE_URL,
            secure,
            'Strict',
        );

        window.$cookies.set(
            'lang',
            user.lang,
            '1y',
            '/',
            process.env.VUE_CLI_BASE_URL,
            secure,
        );
    },
    // Logout user
    resetAuthState(state) {
        // Reset state to default
        Object.assign(state, getDefaultState(state.user.lang));
    },
    // Set user team list
    setTeamList(state, list) {
        state.user.teams = list;
        if (state.user.teams.length === 1) {
            state.selectedTeamIndex = 0;
        } else {
            state.selectedTeamIndex = -1;
        }
    },
    setTeams(state, data) {
        state.user.teams = data.teams;
        state.user.teamPagination = data.pagination;
    },
    setSelectedTeam(state, selectedTeam) {
        state.selectedTeamIndex = state.user.teams.findIndex(team => team.id === selectedTeam.id);
    },
    setInvitedTeamInfo(state, obj) {
        state.invitedTeam = {};
        state.invitedTeam.id = obj.team.id;
        state.invitedTeam.name = obj.team.name;
        state.invitedTeam.invitationCode = obj.invitationCode;
    },
    setInvitedUserInfo(state, obj) {
        state.invitedUser = {};
        state.invitedUser.id = obj.user.id;
        state.invitedUser.firstName = obj.user.profile.firstName;
        state.invitedUser.lastName = obj.user.profile.lastName;
        state.invitedUser.email = obj.user.profile.email;
        state.invitedUser.preferences = obj.user.preferences;
    },
    setResetPwdCode(state, value) {
        state.resetPwdCode = value;
    },
    setLoggedIn(state, value) {
        state.isLoggedIn = value;
    },
    setFirstLoggedInSession(state, value) {
        state.isFirstLoginSession = value;
    },
    set(state, value) {
        state.isLoggedIn = value;
    },
    setLang(state, lang) {
        state.user.lang = lang;
    },
    setAccessToken(state, obj) {
        // Save access token in secure cookie
        window.$cookies.set(
            'accessToken',
            obj.token,
            '7d',
            '/',
            process.env.VUE_CLI_BASE_URL,
            obj.secure,
            'Strict',
        );
        // Decode token without storing it, store userID and token expiration
        const decodedToken = JSON.parse(window.atob(obj.token.split('.')[1]));
        state.tokenExpiration = decodedToken.exp;
        state.user.id = decodedToken.user.id;
    },

    setSignupVerificationCode(state, code) {
        state.verificationCode = code;
    },
    setCurrentMember(state, member) {
        state.currentMember = member;
    },
    setCypress(state) {
        window.$cookies.set(
            'cypressTeamId',
            `${state.user.teams[0].id}`, // we are using the first team for now
            '1y',
            '/',
            process.env.VUE_CLI_BASE_URL,
            true,
        );
    },
};

const actions = {
    setLang({ commit, getters }, newLang) {
        const lang = newLang || getters.user.lang || window.$cookies.get('lang');
        this.$i18n.locale = lang;
        window.$cookies.set(
            'lang',
            lang,
            '1y',
            '/',
            process.env.VUE_CLI_BASE_URL,
            true,
        );
        this.$rq.setAxiosHeaders({
            'X-AQ-Locale-Identifier': lang,
        });
        commit('setLang', lang);
    },
    async login({ dispatch, commit }, user) {
        dispatch('setLang');
        let userData;
        commit('authRequest');
        const axiosHeaders = {
            'X-AQ-Locale-Identifier': user.lang,
        };

        const response = await this.$rq.login(user);
        if (response) {
            userData = response.data;
            const setUser = {
                email: user.email,
                id: userData.id,
                lang: user.lang,
                isFirstLogin: userData.isFirstLogin,
            };
            commit({
                type: 'authSuccess',
                refreshToken: userData.refreshToken,
                user: setUser,
                headers: axiosHeaders,
                secure: process.env.NODE_ENV !== 'development',
            });
            commit({
                type: 'setAccessToken',
                token: userData.accessToken,
                secure: process.env.NODE_ENV !== 'development',
            });
            const urlParams = new URLSearchParams(window.location.search);
            const sso = urlParams.get('sso');
            const sig = urlParams.get('sig');

            if (sso && sig) {
                try {
                    const ssoResponse = await this.$rq.loginSso({ sso, sig });
                    if (ssoResponse && ssoResponse.data && ssoResponse.data.redirectUrl) {
                        window.location.href = ssoResponse.data.redirectUrl;
                        return;
                    }
                } catch (ssoError) {
                    console.error('SSO Error:', ssoError);
                    // Continue with normal flow if SSO fails
                }
            }
            await this.dispatch('refreshUserTeams');
            commit('setLoggedIn', true);
            commit('setFirstLoggedInSession', true);
        }
        localStorage.setItem('loginEvent', Date.now());
    },
    setFirstLoggedInSession({ commit }, value) {
        commit('setFirstLoggedInSession', value);
    },
    async refreshAccessTokenAndReloadAccessControlState({ commit, dispatch }) {
        const urlParams = new URLSearchParams(window.location.search);
        const sso = urlParams.get('sso');
        const sig = urlParams.get('sig');
        if (sso && sig) {
            try {
                const ssoResponse = await this.$rq.loginSso({ sso, sig });
                if (ssoResponse && ssoResponse.data && ssoResponse.data.redirectUrl) {
                    window.location.href = ssoResponse.data.redirectUrl;
                    return;
                }
            } catch (ssoError) {
                console.error('SSO Error:', ssoError);
            }
        } else {
            commit({
                type: 'setIsStateAccessControlLoaded',
                isStateAccessControlLoaded: false,
            }, { root: true });
            await this.$rq.getRenewToken();
            await dispatch('setAccessControlInfo', { root: true });
        }
    },
    async refreshUserTeams({ commit, dispatch }) {
        const { data } = await this.$rq.getUserTeams();
        commit('setTeamList', data.teams);
        // if user belongs to just one team means we can select it and set its props for further use
        // otherwise it would be switch team that set its props
        if (data.teams.length === 1) {
            const [team] = data.teams;
            dispatch('reloadTeam', team.id);
        }
        commit('setLoggedIn', true);
    },
    // as Cypress does not keep the store after each test
    // we need to keep track of TeamId to refresh the token with the backend
    async setCypressData({ commit }) {
        commit('setCypress');
    },
    async reloadTeams({ commit }, params) {
        const { data } = await this.$rq.getTeams(params);
        commit('setTeams', data);
    },
    async logout({ commit }) {
        // Delete auth cookies
        const cookiesKey = window.$cookies.keys();
        cookiesKey.forEach(value => {
            if (value !== 'lang') {
                window.$cookies.remove(value);
            }
        });
        // Delete axios headers
        this.$rq.resetAxiosHeaders();
        localStorage.setItem('logoutEvent', Date.now());
        // We have to reset current lang
        commit('resetAuthState');
        await this.dispatch('resetAccessControlState', null, { root: true });
    },
    // Exists to call commit on event
    switchTeam({ commit }, index) {
        commit('setSelectedTeam', index);
    },
    async verifySignupToken(state, data) {
        const decodedData = JSON.parse(window.atob(data));
        try {
            await this.$rq.getVerificationCodeStatus(decodedData.verificationCode, decodedData.user.id);
            return true;
        } catch (e) {
            return false;
        }
    },
    decodeUrlInviteData({
        commit,
    }, data) {
        let valid = false;
        if (data != null) {
            const decodedData = JSON.parse(Base64.decode(data));
            if (decodedData.team != null && decodedData.invitationCode != null) {
                valid = true;
                commit('setInvitedTeamInfo', decodedData);
                commit('setInvitedUserInfo', decodedData);
                commit('setSignupVerificationCode', decodedData.verificationCode);
            }
            if (decodedData.user != null) {
                valid = true;
            }
        }
        return valid;
    },
    decodeUrlResetPwd({
        commit,
    }, data) {
        let valid = false;
        if (data != null) {
            valid = true;
            commit('setResetPwdCode', data);
        }
        return valid;
    },

    // Automatically handle token on application start / page refresh
    async checkAuthOnAppInit({ commit, dispatch, getters }) {
    // App 1 encodes cookies with encodeURIComponent, so decode them here
        const rawAccessToken = window.$cookies ? window.$cookies.get('accessToken') : undefined;
        const rawRefreshToken = window.$cookies ? window.$cookies.get('refreshToken') : undefined;

        const accessToken = rawAccessToken ? decodeURIComponent(rawAccessToken) : undefined;
        const refreshToken = rawRefreshToken ? decodeURIComponent(rawRefreshToken) : undefined;

        if (!accessToken || !refreshToken) {
            commit('resetAuthState');
            return;
        }

        try {
        // Validate access token
            const decoded = JSON.parse(window.atob(accessToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            if (decoded.exp <= currentTime + 60) {
                try {
                    await dispatch('refreshAccessTokenAndReloadAccessControlState');
                } catch (refreshError) {
                    commit('resetAuthState');
                    return;
                }
            }

            // Restore user info from token (use fresh token after potential refresh)
            const currentTokenRaw = window.$cookies ? window.$cookies.get('accessToken') : undefined;
            const currentToken = currentTokenRaw ? decodeURIComponent(currentTokenRaw) : undefined;
            if (!currentToken) {
                commit('resetAuthState');
                return;
            }
            const tokenData = JSON.parse(window.atob(currentToken.split('.')[1]));
            commit('restoreUserFromToken', tokenData);

            commit('setLoggedIn', true);
            this.$rq.setAxiosHeaders({
                Authorization: `Bearer ${ currentToken}`,
            });
            await dispatch('refreshUserTeams');

            // Restore selected team - use tokenData (fresh), not decoded (stale)
            const savedTeamId = window.$cookies ? window.$cookies.get('currentTeamId') : undefined;
            const tokenTeamId = tokenData.team ? tokenData.team.id : undefined;
            const teamIdToRestore = savedTeamId || tokenTeamId;
            if (teamIdToRestore) {
                const teams = getters.teams || [];
                const savedTeam = teams.find(t => t.id === teamIdToRestore);
                if (savedTeam) {
                    commit('setSelectedTeam', savedTeam);
                }
            }

            await dispatch('setAccessControlInfo', null, { root: true });
        } catch (error) {
            commit('resetAuthState');
        }
    },

};

const getters = {
    isLoggedIn: state => state.isLoggedIn,
    isFirstLoginSession: state => state.isFirstLoginSession,
    authStatus: state => state.status,
    user: state => state.user,
    currentMember: state => state.currentMember,
    email: state => state.user.email,
    teams: state => state.user.teams,
    teamPagination: state => state.user.teamPagination,
    teamsCount: state => state.user.teams.length,
    teamIndex: state => state.selectedTeamIndex,
    invitedTeam: state => state.invitedTeam,
    resetPwdCode: state => state.resetPwdCode,
    userID: state => state.user.id,
    tokenExpiration: state => state.tokenExpiration,
    verificationCode: state => state.verificationCode,
    invitedUser: state => state.invitedUser,
    defaultUserRoute: state => {
        if (!state.isLoggedIn) {
            return 'login';
        }
        const teamsNum = state.user.teams.length;
        if (teamsNum === 0) {
            return 'profile';
        } if (state.selectedTeamIndex === -1) {
            return 'teams';
        }

        if (Array.isArray(state?.currentMember?.roles) && state.currentMember.roles[0].key === roles.FINANCIAL_ADMINISTRATOR.value) {
            return 'administration';
        }

        return 'clients';
    },
    team: state => {
        if (
            state.selectedTeamIndex !== -1
            && typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'
        ) {
            return state.user.teams[state.selectedTeamIndex];
        }
        return {};
    },
    teamID: state => {
        if (state.selectedTeamIndex !== -1 && typeof state.user.teams[state.selectedTeamIndex] !== 'undefined') {
            return state.user.teams[state.selectedTeamIndex].id;
        }
        return false;
    },
    teamName: state => {
        if (
            state.selectedTeamIndex !== -1
            && typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'
        ) {
            return state.user.teams[state.selectedTeamIndex].name;
        }
        return '';
    },

    hasSelectedTeam: state => (state.selectedTeamIndex !== -1 && typeof state.user.teams[state.selectedTeamIndex] !== 'undefined'),
};

export default {
    state: defaultState,
    getters,
    actions,
    mutations,
};
